import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LocalDate } from "@/components/ui/local-date";
import { AGENT_AVATAR_BY_SLUG } from "@/lib/agent-avatars";
import { formatTokens, formatUsd } from "@/lib/agents";
import { api } from "@/lib/api";
import { Link, Navigate, createFileRoute, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

/**
 * `/agents` — admin-only. The AI workforce: one card per agent with its
 * portrait, what it is for, what it burns and the switch to pause it.
 *
 * The whole card opens the agent, the way a project card does — a link
 * on the name alone makes people aim at four words when the obvious
 * target is the card. The pause button is therefore a sibling of the
 * link rather than inside it: a button nested in an anchor is invalid
 * markup and would navigate on click.
 *
 * E2E behaviour contract lives in `e2e/agents.spec.ts`.
 */

type AgentCard = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  status: "active" | "paused";
  lastRunAt: string | null;
  today: { tokens: number; costUsd: number };
  month: { tokens: number; costUsd: number };
  pendingSuggestions: number;
  billing: "metered" | "subscription";
  limitedUntil: string | null;
  accessError: string | null;
};

export const Route = createFileRoute("/_app/agents/")({
  component: AgentsPage,
});

function AgentsPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const [agents, setAgents] = useState<AgentCard[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = user.globalRole === "admin";

  const load = useCallback(
    () =>
      api
        .get<{ agents: AgentCard[] }>("/api/agents")
        .then((r) => setAgents(r.agents))
        .catch(() => setError("Couldn't load the agents.")),
    [],
  );

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  if (!isAdmin) return <Navigate to="/" />;

  async function setStatus(slug: string, status: "active" | "paused") {
    setAgents((prev) => prev?.map((a) => (a.slug === slug ? { ...a, status } : a)) ?? null);
    await api.patch(`/api/agents/${encodeURIComponent(slug)}`, { status }).catch(() => void load());
  }

  return (
    <div className="w-full space-y-6">
      <div>
        <h1 className="font-bold text-3xl">Agents</h1>
        <p className="mt-1 text-muted-foreground">
          The AI workforce: what each agent is doing, what it burns, and the switch to pause it.
        </p>
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {agents?.map((a) => (
          <AgentTile
            key={a.slug}
            agent={a}
            onToggle={() => setStatus(a.slug, a.status === "active" ? "paused" : "active")}
          />
        ))}
      </div>

      {agents && agents.length === 0 && (
        <p className="text-muted-foreground text-sm">No agents registered.</p>
      )}
    </div>
  );
}

function AgentTile({ agent: a, onToggle }: { agent: AgentCard; onToggle: () => void }) {
  return (
    // `h-full` on both: the grid stretches the wrapper, and without it
    // on the link the card's border would stop at its own content and
    // the row would look ragged.
    <div className="relative h-full" data-testid="agent-card">
      <Link
        to="/agents/$slug"
        params={{ slug: a.slug }}
        className="block h-full rounded-lg border bg-card p-5 transition-colors hover:bg-accent/30"
        data-testid="agent-open"
      >
        <div className="flex items-start gap-4">
          <Avatar src={AGENT_AVATAR_BY_SLUG[a.slug]} name={a.name} size={88} />
          <div className="min-w-0 flex-1">
            {/* Right padding leaves the pause button its corner. */}
            <h2 className="pr-20 font-bold font-heading text-xl leading-snug">{a.name}</h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <Badge
                variant={a.status === "active" ? "default" : "secondary"}
                data-testid="agent-status"
              >
                {a.status === "active" ? "Active" : "Paused"}
              </Badge>
              {a.limitedUntil && (
                <Badge variant="secondary" data-testid="agent-limited">
                  Waiting for limit
                </Badge>
              )}
              {a.accessError && (
                <Badge variant="destructive" data-testid="agent-access-error">
                  Token refused
                </Badge>
              )}
              {a.pendingSuggestions > 0 && (
                <Badge variant="warning" data-testid="agent-pending">
                  {a.pendingSuggestions} pending
                </Badge>
              )}
            </div>
          </div>
        </div>

        {a.description && (
          <p className="mt-3 line-clamp-3 text-muted-foreground text-sm">{a.description}</p>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3 border-t pt-3">
          <div>
            <div className="font-semibold text-sm leading-none" data-testid="agent-today">
              {formatTokens(a.today.tokens)} tok · {formatUsd(a.today.costUsd)}
            </div>
            <div className="mt-1.5 text-muted-foreground text-xs">today</div>
          </div>
          <div>
            <div className="font-semibold text-sm leading-none" data-testid="agent-month">
              {formatTokens(a.month.tokens)} tok · {formatUsd(a.month.costUsd)}
            </div>
            <div className="mt-1.5 text-muted-foreground text-xs">
              this month
              {a.billing === "subscription" && (
                <span data-testid="agent-billing"> · estimate, subscription</span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-3 text-muted-foreground text-xs">
          Last activity: {a.lastRunAt ? <LocalDate value={a.lastRunAt} withTime /> : "never"}
        </div>
      </Link>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="absolute top-4 right-4"
        onClick={onToggle}
        data-testid="agent-toggle"
      >
        {a.status === "active" ? "Pause" : "Resume"}
      </Button>
    </div>
  );
}
